import type {
  AppliedDiscount,
  CartItem,
  FeeStructure,
  SelectedSeat,
  SelectedTable,
  TierDiscount,
} from '../types';
import { applyPromoRespectingFixedFeesCents, splitFeePreviewUnitToDiscountableAndFixedCents } from './feePreview';

/**
 * All-in list price in cents: `fee_preview.total_with_fees` when present, else `tier.fee` (list).
 */
export function allInListPriceCentsForTier(
  tier: Pick<FeeStructure, 'fee' | 'fee_preview'>,
): number {
  const tw = tier.fee_preview?.total_with_fees;
  if (tw != null && Number.isFinite(tw)) return Math.round(tw);
  return Math.round(tier.fee);
}

/**
 * Price after sponsor % / fixed on **list base only** when `fee_preview` is present; fees+tax from preview unchanged.
 */
export function estimateSponsorDiscountedCentsForTier(
  tier: Pick<FeeStructure, 'fee' | 'fee_preview' | 'sponsur_free_tier'>,
  sponsorDiscount: { discount: number; discount_type: string } | null | undefined,
): number {
  if (tier.sponsur_free_tier) return 0;
  if (!sponsorDiscount) return Math.round(tier.fee);
  if (sponsorDiscount.discount >= 100) return 0;
  const fp = tier.fee_preview;
  if (fp?.total_with_fees != null && Number.isFinite(fp.total_with_fees)) {
    const { discountable, fixed } = splitFeePreviewUnitToDiscountableAndFixedCents(
      fp.total_with_fees,
      fp.fees_total,
      fp.tax_total,
      Math.round(tier.fee),
    );
    const t = (sponsorDiscount.discount_type || 'percentage').toLowerCase();
    if (t === 'fixed') {
      return applyPromoRespectingFixedFeesCents(discountable, fixed, {
        type: 'fixed',
        value: sponsorDiscount.discount,
      });
    }
    return applyPromoRespectingFixedFeesCents(discountable, fixed, {
      type: 'percentage',
      value: sponsorDiscount.discount,
    });
  }
  if ((sponsorDiscount.discount_type || 'percentage').toLowerCase() === 'fixed') {
    return Math.max(0, Math.round(tier.fee) - Math.round(sponsorDiscount.discount));
  }
  return Math.round(tier.fee * (1 - sponsorDiscount.discount / 100));
}

function getActiveDiscount(discounts: TierDiscount[]): TierDiscount | null {
  const now = Date.now();
  return (
    discounts.find(
      (d) => new Date(d.start_date).getTime() <= now && new Date(d.end_date).getTime() >= now,
    ) || null
  );
}

/**
 * List / sponsor / tier price before cart-level promo (amounts in **cents** for `fee`).
 * Exported for ticket UI and fee-preview checks.
 */
/**
 * Subtotal in cents for one cart line. Prefers server `fee_preview.total_with_fees` (all-in) when the
 * tier is at list price (no sponsor / tier % override) so cart-level promos apply after fee preview, not to base + sales-calc.
 */
export function getTicketLineSubtotalCents(
  c: CartItem,
  unlockedSponsorTiers: Record<string, { value: number; type: string; code: string }>,
): number {
  const sponsorUnlock = unlockedSponsorTiers[c.tier._id];
  if (sponsorUnlock) {
    const isFree = c.tier.sponsur_free_tier || sponsorUnlock.value >= 100;
    if (isFree) return 0;
    const perUnit = estimateSponsorDiscountedCentsForTier(c.tier, {
      discount: sponsorUnlock.value,
      discount_type: sponsorUnlock.type,
    });
    return perUnit * c.quantity;
  }
  const { effective } = getDisplayPrice(c.tier, false);
  if (effective !== c.tier.fee) {
    return effective * c.quantity;
  }
  const tw = c.tier.fee_preview?.total_with_fees;
  if (tw != null && Number.isFinite(tw)) {
    return Math.round(tw) * c.quantity;
  }
  return c.tier.fee * c.quantity;
}

export function sumTicketLinesCents(
  cart: CartItem[],
  unlockedSponsorTiers: Record<string, { value: number; type: string; code: string }>,
): number {
  return cart.reduce((sum, c) => sum + getTicketLineSubtotalCents(c, unlockedSponsorTiers), 0);
}

export function getDisplayPrice(
  tier: FeeStructure,
  sponsorUnlocked: boolean,
): { original: number; effective: number; discountLabel: string | null } {
  const sponsorDiscount = tier.sponsor_discount;
  const isFree = tier.sponsur_free_tier || (sponsorDiscount && sponsorDiscount.discount >= 100);

  if (sponsorUnlocked && (isFree || sponsorDiscount)) {
    if (isFree) {
      return { original: allInListPriceCentsForTier(tier), effective: 0, discountLabel: 'Sponsored Free' };
    }
    if (sponsorDiscount) {
      const effective = estimateSponsorDiscountedCentsForTier(tier, sponsorDiscount);
      const original = allInListPriceCentsForTier(tier);
      const dt = (sponsorDiscount.discount_type || 'percentage').toLowerCase();
      const label =
        dt === 'fixed'
          ? 'Sponsor offer'
          : `${sponsorDiscount.discount}% Off`;
      return { original, effective, discountLabel: label };
    }
  }

  const activeDiscount = getActiveDiscount(tier.discounts);
  if (activeDiscount) {
    const effective = Math.round(tier.fee * (1 - activeDiscount.discount_percentage / 100));
    return { original: tier.fee, effective, discountLabel: activeDiscount.discount_name };
  }
  return { original: tier.fee, effective: tier.fee, discountLabel: null };
}

/**
 * True when we can use server `fee_preview` for all ticket lines: all-in / total_with_fees,
 * no `calculateFees`, no local fee/tax lines.
 * Every line must have `fee_preview.total_with_fees` so totals are all-in (no `sales-calc`).
 * Sponsor unlock is OK: % / fixed apply to list base only; per-ticket fees+tax from preview stay fixed.
 */
export function canUseFeePreviewForTicketsCart(
  cart: CartItem[],
  unlockedSponsorTiers: Record<string, { value: number; type: string; code: string }>,
): boolean {
  if (cart.length === 0) return false;
  for (const c of cart) {
    if (unlockedSponsorTiers[c.tier._id]) {
      const tw = c.tier.fee_preview?.total_with_fees;
      if (tw == null || !Number.isFinite(tw)) return false;
      continue;
    }
    const { effective } = getDisplayPrice(c.tier, false);
    if (effective !== c.tier.fee) return false;
    const tw = c.tier.fee_preview?.total_with_fees;
    if (tw == null || !Number.isFinite(tw)) return false;
  }
  return true;
}

export function sumFeePreviewAllInCents(cart: CartItem[], unlocked: Record<string, { value: number; type: string; code: string }> = {}): number {
  return sumTicketLinesCents(cart, unlocked);
}

/** Per-seat: prefer `fee_preview.total_with_fees` (cents) when list price is used; else `rule.price` (cents). */
export function sumSeatLinesCentsPreferPreview(seats: SelectedSeat[]): number {
  return seats.reduce((sum, s) => {
    const rule = s.pricingRule;
    if (!rule) return sum;
    const p = rule.price ?? 0;
    if (p === 0) return sum;
    const tw = rule.fee_preview?.total_with_fees;
    if (tw != null && Number.isFinite(tw)) return sum + Math.round(tw);
    return sum + p;
  }, 0);
}

export function canUseFeePreviewForSeatsSelected(seats: SelectedSeat[]): boolean {
  if (seats.length === 0) return true;
  for (const s of seats) {
    const rule = s.pricingRule;
    const p = rule?.price ?? 0;
    if (p > 0) {
      const tw = rule?.fee_preview?.total_with_fees;
      if (tw == null || !Number.isFinite(tw)) return false;
    }
  }
  return true;
}

/**
 * True when every paid table line has server `fee_preview.total_with_fees` (all-in cents).
 * When true, we prefer that sum over `tableReservationHoldCents` (hold `pricing.total` can be list-only or stale).
 */
export function selectedTablesHaveFeePreviewAllIn(selectedTables: SelectedTable[]): boolean {
  let anyPaid = false;
  for (const t of selectedTables) {
    const rule = t.pricingRule;
    const major = rule?.price ?? 0;
    if (major <= 0) continue;
    anyPaid = true;
    const tw = rule?.fee_preview?.total_with_fees;
    if (tw == null || !Number.isFinite(tw)) return false;
  }
  return anyPaid;
}

/**
 * Table `pricingRule.price` is in major units.
 * Prefer per-line `fee_preview.total_with_fees` when every paid line has it; else `tableReservationHoldCents` from
 * event_tables hold; else sum `price` in major → cents.
 */
export function sumTableLinesCentsPreferPreview(
  selectedTables: SelectedTable[],
  tableReservationHoldCents: number | null,
): number {
  if (selectedTablesHaveFeePreviewAllIn(selectedTables)) {
    return selectedTables.reduce((sum, t) => {
      const rule = t.pricingRule;
      if (!rule || rule.price <= 0) return sum;
      const tw = rule.fee_preview?.total_with_fees;
      if (tw != null && Number.isFinite(tw)) return sum + Math.round(tw);
      return sum + Math.round(rule.price * 100);
    }, 0);
  }
  if (tableReservationHoldCents != null) {
    return Math.round(tableReservationHoldCents);
  }
  return selectedTables.reduce((sum, t) => {
    const rule = t.pricingRule;
    if (!rule) return sum;
    if (rule.price <= 0) return sum;
    const tw = rule.fee_preview?.total_with_fees;
    if (tw != null && Number.isFinite(tw)) return sum + Math.round(tw);
    return sum + Math.round(rule.price * 100);
  }, 0);
}

export function canUseFeePreviewForTablesSelected(
  selectedTables: SelectedTable[],
  tableReservationHoldCents: number | null,
): boolean {
  if (tableReservationHoldCents != null) {
    return Number.isFinite(tableReservationHoldCents) && tableReservationHoldCents >= 0;
  }
  if (selectedTables.length === 0) return true;
  for (const t of selectedTables) {
    const rule = t.pricingRule;
    const major = rule?.price ?? 0;
    if (major > 0) {
      const tw = rule?.fee_preview?.total_with_fees;
      if (tw == null || !Number.isFinite(tw)) return false;
    }
  }
  return true;
}

/**
 * When true, the whole order (tickets, seats, tables / hold) uses API `fee_preview` or server hold totals only —
 * skip `POST /mixed/sales-calc` and do not show separate service fee / tax lines.
 */
export function canUseFeePreviewForFullOrder(
  cart: CartItem[],
  unlockedSponsorTiers: Record<string, { value: number; type: string; code: string }>,
  selectedSeats: SelectedSeat[],
  selectedTables: SelectedTable[],
  tableReservationHoldCents: number | null,
): boolean {
  if (cart.length > 0) {
    if (!canUseFeePreviewForTicketsCart(cart, unlockedSponsorTiers)) return false;
  }
  if (!canUseFeePreviewForSeatsSelected(selectedSeats)) return false;
  if (!canUseFeePreviewForTablesSelected(selectedTables, tableReservationHoldCents)) return false;
  return true;
}

/** All-in / list cents for seats; prefers `fee_preview` when present. */
export function sumSeatsAllInCentsFromFeePreview(seats: SelectedSeat[]): number {
  return sumSeatLinesCentsPreferPreview(seats);
}

/**
 * All-in table total in cents. If `tableReservationHoldCents` is set, that value is the full table order (cents).
 * Otherwise each selected table with positive list price must have `fee_preview.total_with_fees` (cents).
 */
export function sumTablesAllInCentsFromFeePreview(
  selectedTables: SelectedTable[],
  tableReservationHoldCents: number | null,
): number {
  return sumTableLinesCentsPreferPreview(selectedTables, tableReservationHoldCents);
}

/**
 * `discount.value` is raw **cents** for fixed-amount promos (same as `tier.fee` units).
 * Applies to full subtotal (legacy) — prefer {@link orderTotalAfterCartPromoCents} when `fee_preview` is present.
 */
export function applyPromoDiscountCents(
  subtotalCents: number,
  discount: AppliedDiscount | null,
): number {
  if (!discount) return subtotalCents;
  if (discount.type === 'percentage') {
    return Math.round(subtotalCents * (1 - discount.value / 100));
  }
  return Math.max(0, subtotalCents - Math.round(discount.value));
}

export function getTicketLineDiscountableAndFixedCents(
  c: CartItem,
  unlockedSponsorTiers: Record<string, { value: number; type: string; code: string }>,
): { discountable: number; fixed: number } {
  const sponsorUnlock = unlockedSponsorTiers[c.tier._id];
  if (sponsorUnlock) {
    const isFree = c.tier.sponsur_free_tier || sponsorUnlock.value >= 100;
    if (isFree) {
      return { discountable: 0, fixed: 0 };
    }
    const fp = c.tier.fee_preview;
    if (fp?.total_with_fees != null && Number.isFinite(fp.total_with_fees)) {
      const { discountable: d, fixed: f } = splitFeePreviewUnitToDiscountableAndFixedCents(
        fp.total_with_fees,
        fp.fees_total,
        fp.tax_total,
        Math.round(c.tier.fee),
      );
      const t = (sponsorUnlock.type || 'percentage').toLowerCase();
      const promo: { type: 'percentage' | 'fixed'; value: number } =
        t === 'fixed'
          ? { type: 'fixed', value: sponsorUnlock.value }
          : { type: 'percentage', value: sponsorUnlock.value };
      const perUnit = applyPromoRespectingFixedFeesCents(d, f, promo);
      const listAfterSponsor = perUnit - f;
      return { discountable: listAfterSponsor * c.quantity, fixed: f * c.quantity };
    }
    const t = (sponsorUnlock.type || 'percentage').toLowerCase();
    if (t === 'fixed') {
      const p = Math.max(0, Math.round(c.tier.fee) - Math.round(sponsorUnlock.value));
      return { discountable: p * c.quantity, fixed: 0 };
    }
    const price = Math.round(c.tier.fee * (1 - sponsorUnlock.value / 100));
    return { discountable: price * c.quantity, fixed: 0 };
  }
  const { effective } = getDisplayPrice(c.tier, false);
  if (effective !== c.tier.fee) {
    return { discountable: effective * c.quantity, fixed: 0 };
  }
  const fp = c.tier.fee_preview;
  if (fp?.total_with_fees != null && Number.isFinite(fp.total_with_fees)) {
    const { discountable, fixed } = splitFeePreviewUnitToDiscountableAndFixedCents(
      fp.total_with_fees,
      fp.fees_total,
      fp.tax_total,
      Math.round(c.tier.fee),
    );
    return { discountable: discountable * c.quantity, fixed: fixed * c.quantity };
  }
  return { discountable: c.tier.fee * c.quantity, fixed: 0 };
}

function getSeatLineDiscountableAndFixedCents(s: SelectedSeat): { discountable: number; fixed: number } {
  const rule = s.pricingRule;
  if (!rule) return { discountable: 0, fixed: 0 };
  const p = rule.price ?? 0;
  if (p === 0) return { discountable: 0, fixed: 0 };
  const fp = rule.fee_preview;
  if (fp?.total_with_fees != null && Number.isFinite(fp.total_with_fees)) {
    const { discountable, fixed } = splitFeePreviewUnitToDiscountableAndFixedCents(
      fp.total_with_fees,
      fp.fees_total,
      fp.tax_total,
      Math.round(p),
    );
    return { discountable, fixed };
  }
  return { discountable: p, fixed: 0 };
}

function getTableLineDiscountableAndFixedCents(t: SelectedTable): { discountable: number; fixed: number } {
  const rule = t.pricingRule;
  if (!rule) return { discountable: 0, fixed: 0 };
  if (rule.price <= 0) return { discountable: 0, fixed: 0 };
  const fp = rule.fee_preview;
  const listC = Math.round(rule.price * 100);
  if (fp?.total_with_fees != null && Number.isFinite(fp.total_with_fees)) {
    const { discountable, fixed } = splitFeePreviewUnitToDiscountableAndFixedCents(
      fp.total_with_fees,
      fp.fees_total,
      fp.tax_total,
      listC,
    );
    return { discountable, fixed };
  }
  return { discountable: listC, fixed: 0 };
}

export function sumOrderDiscountableAndFixedCents(
  cart: CartItem[],
  unlocked: Record<string, { value: number; type: string; code: string }>,
  seats: SelectedSeat[],
  tables: SelectedTable[],
  tableReservationHoldCents: number | null,
): { discountable: number; fixed: number } {
  let discountable = 0;
  let fixed = 0;
  for (const c of cart) {
    const o = getTicketLineDiscountableAndFixedCents(c, unlocked);
    discountable += o.discountable;
    fixed += o.fixed;
  }
  for (const s of seats) {
    const o = getSeatLineDiscountableAndFixedCents(s);
    discountable += o.discountable;
    fixed += o.fixed;
  }
  if (selectedTablesHaveFeePreviewAllIn(tables)) {
    for (const t of tables) {
      const o = getTableLineDiscountableAndFixedCents(t);
      discountable += o.discountable;
      fixed += o.fixed;
    }
  } else if (tableReservationHoldCents != null) {
    discountable += Math.round(tableReservationHoldCents);
  } else {
    for (const t of tables) {
      const o = getTableLineDiscountableAndFixedCents(t);
      discountable += o.discountable;
      fixed += o.fixed;
    }
  }
  return { discountable, fixed };
}

/**
 * % / fixed off **list base** only; fees+tax from `fee_preview` unchanged.
 * If the split does not reconcile to `prePromoAllInCents` or there is no separable fixed portion, falls back to {@link applyPromoDiscountCents} on the full all-in subtotal.
 */
export function orderTotalAfterCartPromoCents(
  prePromoAllInCents: number,
  split: { discountable: number; fixed: number },
  discount: AppliedDiscount | null,
): number {
  if (!discount) return prePromoAllInCents;
  const { discountable, fixed } = split;
  if (Math.round(discountable + fixed) !== Math.round(prePromoAllInCents)) {
    return applyPromoDiscountCents(prePromoAllInCents, discount);
  }
  if (fixed === 0) {
    return applyPromoDiscountCents(prePromoAllInCents, discount);
  }
  return applyPromoRespectingFixedFeesCents(discountable, fixed, discount);
}
